import { useMemo } from 'react';
import { useAppSelector } from './redux';
import {
  selected as selectedOrganisation,
  networks as organisationNetworks,
} from '@modules/organisation/selectors/organisation';
import { selected as selectedNetwork } from '@modules/network/selectors/network';
import { EComponentTypes } from '@common/definitions';
import {
  hasEnabledComponent,
  hasEnabledComponentOnAnyNetwork,
  hasEnabledComponentForUserNetworks,
} from '@common/utils/has-enabled-component';
import { useAdminScreen } from './use-admin-screen';

type ComponentTypesKeys = keyof typeof EComponentTypes;
type ComponentTypesValues = typeof EComponentTypes[ComponentTypesKeys];

/**
 * Feature on/off from `enabled_components` only (aligned with mobile).
 *
 * Admin (no selected network): org `enabled_components`.
 * Community (selected network): org or selected network `enabled_components`.
 *
 * Do not use `available_components` here — that list is for settings toggles.
 */
export function useIsComponentEnabled(component: ComponentTypesValues): boolean {
  const isAdminScreen = useAdminScreen();
  const organisation = useAppSelector(selectedOrganisation);
  const network = useAppSelector(selectedNetwork);

  return hasEnabledComponent(
    component,
    isAdminScreen ? undefined : network,
    organisation,
  );
}

/** Org enabled_components or any organisation network. For admin Chat / Flexchange chrome. */
export function useIsComponentEnabledOnAnyNetwork(
  component: ComponentTypesValues,
): boolean {
  const organisation = useAppSelector(selectedOrganisation);
  const networks = useAppSelector(organisationNetworks);

  return hasEnabledComponentOnAnyNetwork(component, networks, organisation);
}

type UserNetworkRef = string | { id: string };

/** Admin: org or this person's communities; missing ids → org or any community. Community: selected context. */
export function useIsComponentEnabledForUserNetworks(
  component: ComponentTypesValues,
  userNetworkRefs?: UserNetworkRef[] | null,
): boolean {
  const isAdminScreen = useAdminScreen();
  const isEnabledOnSelectedContext = useIsComponentEnabled(component);
  const organisation = useAppSelector(selectedOrganisation);
  const allOrganisationNetworks = useAppSelector(organisationNetworks);

  const userNetworkIds = useMemo(() => (
    (userNetworkRefs || [])
      .map((network) => (typeof network === 'string' ? network : network.id))
      .filter(Boolean)
  ), [userNetworkRefs]);

  if (!isAdminScreen) return isEnabledOnSelectedContext;

  return hasEnabledComponentForUserNetworks(
    component,
    organisation,
    allOrganisationNetworks,
    userNetworkIds,
  );
}

